import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { selectOrganizationsSelectedProvider } from '../organizationsManagementSelectors'
import { selectNettingTarget } from './nettingUpdaterSelectors'

const useNettingBatches = () => {
  const target = useSelector(selectNettingTarget)
  const organizationProvider = useSelector(selectOrganizationsSelectedProvider)
  const selectedProvider = target ?? organizationProvider
  const providerId = selectedProvider?.id || null

  // Batches are loaded with the organization data
  const initialBatches = selectedProvider?.netting_batches || []
  const [batches, setBatches] = useState(initialBatches)
  const [batchErrors, setBatchErrors] = useState([])

  // Sync when provider data changes (e.g. navigation between providers)
  useEffect(() => {
    setBatches(selectedProvider?.netting_batches || [])
    setBatchErrors([])
  }, [selectedProvider?.netting_batches])

  const updateBatchProgress = useCallback((batchData) => {
    setBatches(prev => {
      const idx = prev.findIndex(b => b.id === batchData.id)
      if (idx >= 0) {
        const updated = [...prev]
        updated[idx] = { ...updated[idx], ...batchData }
        return updated
      }
      // New batch: prepend
      return [batchData, ...prev]
    })
  }, [])

  const addBatchError = useCallback((errorData) => {
    setBatchErrors(prev => [errorData, ...prev])
  }, [])

  const dismissBatchError = useCallback((index) => {
    setBatchErrors(prev => prev.filter((_, i) => i !== index))
  }, [])

  // Listen for batch events via Echo
  useEffect(() => {
    if (!providerId) return

    const channelName = `netting.batches.${providerId}`

    window.Echo.channel(channelName)
      .listen('.netting.batch.updated', (e) => {
        updateBatchProgress(e)
      })
      .listen('.netting.batch.failed', (e) => {
        addBatchError({
          reason: e.reason || '',
          message: e.detail || e.message || e.error || 'Une erreur est survenue',
          timestamp: e.timestamp || new Date().toISOString(),
        })
      })

    return () => {
      window.Echo.leaveChannel(channelName)
    }
  }, [providerId, updateBatchProgress, addBatchError])

  return { batches, batchErrors, dismissBatchError }
}

export default useNettingBatches
